import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Operator, Workshop } from "../types";
import { operatorService, workshopService } from "../services/dataService";
import { poErr } from "../utils/purchasing";
import { SinglePicker, PickColumn, trLower, dash } from "./PickerModal";
import "./ModelModal.css"; // eski genel kurallar başka ekranlarda kullanılıyor; bkz. ModelModal.tsx

interface OperatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (operator: Operator) => void;
  workshopId?: string; // Seçili atölye ID'si - opsiyonel
}

const emptyOperator = { firstName: "", lastName: "", phone: "", email: "", specialization: "", workshopId: "" };
const fullName = (o: Operator) => `${o.firstName} ${o.lastName}`.trim();

const match = (o: Operator, q: string) =>
  trLower(fullName(o)).includes(q) || trLower(o.specialization).includes(q) || trLower(o.workshop?.name).includes(q) || trLower(o.email).includes(q) || (o.phone || "").includes(q);

const OperatorPicker: React.FC<Omit<OperatorModalProps, "isOpen">> = ({ onClose, onSelect, workshopId }) => {
  const [operators, setOperators] = useState<Operator[]>([]);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  // Seçili atölye yeni operatörün varsayılan atölyesi olur
  const [newOperator, setNewOperator] = useState({ ...emptyOperator, workshopId: workshopId || "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    const [ops, wss] = await Promise.allSettled([operatorService.getAll(), workshopService.getAll()]);
    if (ops.status === "fulfilled") setOperators(ops.value);
    else { console.error("Operatörler yüklenemedi:", ops.reason); setLoadError("Operatörler yüklenemedi."); }
    if (wss.status === "fulfilled") setWorkshops(wss.value);
    else console.error("Atölyeler yüklenemedi:", wss.reason);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Atölye seçiliyse sadece o atölyedeki operatörleri göster
  const filteredOperators = useMemo(
    () => (workshopId ? operators.filter((op) => op.workshopId === workshopId) : operators),
    [operators, workshopId],
  );
  const workshopName = workshopId ? workshops.find((w) => w.workshopId === workshopId)?.name : undefined;
  const wsName = (o: Operator) => o.workshop?.name || workshops.find((w) => w.workshopId === o.workshopId)?.name;

  const columns: PickColumn<Operator>[] = [
    {
      head: "Operatör",
      cell: (o) => (
        <>
          <span className="po-name">{fullName(o)}</span>
          {o.specialization && <div className="po-sub">{o.specialization}</div>}
          {o.phone && <div className="po-sub po-only-sm">{o.phone}</div>}
        </>
      ),
    },
    { head: "Atölye", className: workshopId ? "po-hide-sm" : undefined, cell: (o) => wsName(o) || dash },
    { head: "Telefon", className: "po-hide-sm", has: (o) => !!o.phone, cell: (o) => o.phone || dash },
    { head: "E-posta", className: "po-hide-md", has: (o) => !!o.email, cell: (o) => o.email || dash },
  ];

  const handleAddOperator = async () => {
    setSaving(true);
    setError("");
    try {
      const operatorData: any = {
        firstName: newOperator.firstName.trim(),
        lastName: newOperator.lastName.trim(),
        phone: newOperator.phone || undefined,
        email: newOperator.email || undefined,
        specialization: newOperator.specialization || undefined,
        workshopId: newOperator.workshopId || undefined,
      };
      const created = await operatorService.create(operatorData);
      setNewOperator({ ...emptyOperator, workshopId: workshopId || "" });
      onSelect(created);
      onClose();
    } catch (err: any) {
      setError("Operatör eklenemedi: " + poErr(err, err?.message || "bilinmeyen hata"));
      setSaving(false);
    }
  };

  const set = (k: keyof typeof emptyOperator, v: string) => { setNewOperator((p) => ({ ...p, [k]: v })); setError(""); };

  const notice = workshopId && !loading && !loadError
    ? filteredOperators.length === 0
      ? <div className="po-note warn">Seçili atölyede{workshopName ? <> (<b>{workshopName}</b>)</> : ""} kayıtlı operatör yok. Önce operatör ekleyin.</div>
      : <div className="po-note info">Yalnız <b>{workshopName || "seçili atölyenin"}</b> operatörleri listelenir.</div>
    : undefined;

  return (
    <SinglePicker<Operator>
      kicker={workshopName ? `Operatör · ${workshopName}` : "Operatör"}
      title="Operatör seç"
      noun="operatör"
      items={filteredOperators}
      loading={loading}
      loadError={loadError}
      onRetry={load}
      rowKey={(o) => o.operatorId}
      rowLabel={fullName}
      match={match}
      placeholder="Ad, uzmanlık, atölye veya telefon ara"
      columns={columns}
      notice={notice}
      emptyText="İlk operatörü buradan ekleyebilirsiniz."
      onPick={(o) => { onSelect(o); onClose(); }}
      onClose={onClose}
      create={{
        label: "Yeni operatör",
        title: "Yeni operatör",
        onSubmit: handleAddOperator,
        saving,
        error,
        valid: !!newOperator.firstName.trim() && !!newOperator.lastName.trim(),
        fields: (
          <>
            <div className="po-grid2">
              <label className="po-field">
                <span>Ad<em>zorunlu</em></span>
                <input value={newOperator.firstName} required onChange={(e) => set("firstName", e.target.value)} />
              </label>
              <label className="po-field">
                <span>Soyad<em>zorunlu</em></span>
                <input value={newOperator.lastName} required onChange={(e) => set("lastName", e.target.value)} />
              </label>
              <label className="po-field">
                <span>Telefon</span>
                <input type="tel" value={newOperator.phone} onChange={(e) => set("phone", e.target.value)} placeholder="0532 000 00 00" />
              </label>
              <label className="po-field">
                <span>E-posta</span>
                <input type="email" value={newOperator.email} onChange={(e) => set("email", e.target.value)} />
              </label>
              <label className="po-field">
                <span>Uzmanlık</span>
                <input value={newOperator.specialization} onChange={(e) => set("specialization", e.target.value)} placeholder="Örn. Nakış" />
              </label>
              <label className="po-field">
                <span>Atölye</span>
                <select value={newOperator.workshopId} onChange={(e) => set("workshopId", e.target.value)}>
                  <option value="">Seçilmedi</option>
                  {workshops.map((w) => <option key={w.workshopId} value={w.workshopId}>{w.name}</option>)}
                </select>
              </label>
            </div>
          </>
        ),
      }}
    />
  );
};

/** Operatör seçici: atölye verilmişse yalnız o atölyenin operatörleri; arama, klavye ile seçim ve satır içi yeni operatör formu. */
const OperatorModal: React.FC<OperatorModalProps> = ({ isOpen, ...rest }) => (isOpen ? <OperatorPicker {...rest} /> : null);

export default OperatorModal;
